// Where a room object's base sits vertically (CIN-15126). The stored height of an object is only trusted
// for things that are mounted on purpose; everything else stands on whatever floor is under its footprint,
// in the guest map and in the studio alike. Plan space (mm); the result is mm above the base floor.
// Pure: relative imports only.
import { type Footprint, floorHeightAt, footprintFloorHeight } from '../floor-height.ts';
import { pointInOutline } from '../outline/outline.ts';
import { levelOf } from '../outline/room-geometry.ts';
import type { PlanPoint, RoomGeometry, RoomOutline } from '../outline/types.ts';
import type { TierModel } from '../tiers.ts';

import type { ObjectFloor } from './object-geometry.ts';
import type { RoomObjectKind } from './object-kinds.ts';

/**
 * - `floor`: stands on the floor under it, whatever height was stored (doors, exits, pillars, walls, stairs,
 *   tables, stages, rails and other imported fixtures).
 * - `mounted`: hangs at its stored height above the base floor (speakers on a wall, the projector under the
 *   ceiling); a height of 0 means it stands on the floor, and it is never lower than the floor under it.
 * - `fixed`: the caller owns the height (the screen has its own elevation logic).
 */
export type ObjectMounting = 'floor' | 'mounted' | 'fixed';

export const OBJECT_MOUNTING: Record<RoomObjectKind, ObjectMounting> = {
    screen: 'fixed',
    stage: 'floor',
    stairs: 'floor',
    pillar: 'floor',
    wall: 'floor',
    door: 'floor',
    emergency_exit: 'floor',
    speaker: 'mounted',
    projector: 'mounted',
    table: 'floor',
    barrier: 'floor',
    counter: 'floor',
    railing: 'floor',
    spotlight: 'mounted',
    truss: 'mounted',
    footstool: 'floor',
    other: 'floor'
};

export type ObjectPlacement = Footprint & {
    /** Stored base elevation, mm above the base floor. */
    z: number;
};

/**
 * Base of the object, mm above the base floor. Never below the floor under the footprint (`fixed` aside). In a room
 * with levels (`geometry`) an object stands on the level its footprint center and stored height put it on: a stored
 * height at a balcony's elevation inside its footprint is on the balcony.
 */
export function objectBaseMm(
    kind: RoomObjectKind,
    object: ObjectPlacement,
    tiers: TierModel,
    geometry?: RoomGeometry | null
): number {
    const mounting = OBJECT_MOUNTING[kind] ?? 'floor';
    if (mounting === 'fixed') return object.z;
    const level = geometry ? levelOf(geometry, { x: object.x, y: object.y, z: object.z }) : 'main';
    const floor = footprintFloorHeight(tiers, object, level);
    return mounting === 'floor' ? floor : Math.max(floor, object.z);
}

/**
 * The floor under a railing relative to its base `baseMm` (CIN-15200), for `objectParts`: a railing across the
 * rows of a stepped floor follows the tiers like a stair rail instead of floating at the height of the highest one.
 * `undefined` for every other kind and for a flat room.
 */
export function objectFloorAt(
    kind: RoomObjectKind,
    object: ObjectPlacement,
    baseMm: number,
    tiers: TierModel,
    geometry?: RoomGeometry | null
): ObjectFloor | undefined {
    if (kind !== 'railing' || (tiers.tiers.length === 0 && !tiers.levels?.length)) return undefined;
    const level = geometry ? levelOf(geometry, { x: object.x, y: object.y, z: object.z }) : 'main';
    const radians = (object.rotation * Math.PI) / 180;
    const sin = Math.sin(radians);
    const cos = Math.cos(radians);
    // Object-local plan mm to room plan mm: the clockwise turn of `footprintSamples`.
    return (local: PlanPoint) =>
        (floorHeightAt(
            tiers,
            { x: object.x + local.x * cos + local.y * sin, y: object.y - local.x * sin + local.y * cos },
            level
        ) -
            baseMm) /
        1000;
}

/**
 * Whether the object's center lies behind the back wall (plan y beyond `-depth`): a projector there is in
 * the projection booth, outside the drawn room. With an outline (CIN-15126) the object must lie outside it
 * (the object also needs an `x`); the back wall is wherever the outline ends.
 */
export function isBehindBackWall(
    object: { x?: number; y: number },
    roomDepthMm: number,
    outline?: RoomOutline | null
): boolean {
    if (outline) return !pointInOutline(outline, { x: object.x ?? 0, y: object.y }, 0);
    return object.y < -roomDepthMm;
}
